import { copy } from "../copy";
import Icon from "../components/Icon";
import type { ContestDetail } from "./api";
import { Avatar, Eyebrow, secondaryButton } from "./ui";

type Property = { label: string; value: string; sublabel?: string; testId?: string };

export function PropertyGrid({ properties }: { properties: Property[] }) {
  return (
    <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-3">
      {properties.map((p) => (
        <div key={p.label} data-testid={p.testId} className="min-w-0 rounded-lg border border-ground-edge bg-ground-raised p-3.5">
          <p className="text-[10px] uppercase tracking-[0.18em] text-chrome-dim">{p.label}</p>
          <p className="bf-tabular mt-2 break-words font-headline text-lg uppercase leading-none text-chrome">{p.value}</p>
          {p.sublabel && <p className="mt-1.5 text-[11px] leading-snug text-chrome-dim">{p.sublabel}</p>}
        </div>
      ))}
    </div>
  );
}

function prizeValue(contest: ContestDetail): { label: string; value: string } {
  const d = copy.contest.details;
  const p = contest.prize;
  if (p.kind === "pool" && p.display) return { label: d.pool, value: p.display };
  if (p.kind === "cash" && p.display) return { label: d.cashPrize, value: p.display };
  if (p.kind === "prize" && p.label) return { label: d.prize, value: p.label };
  return { label: d.prize, value: d.noPrize };
}

function admissionValue(contest: ContestDetail): string {
  const d = copy.contest.details;
  const a = contest.admission;
  if (a.kind === "fee" && a.display) return d.entry(a.display);
  if (a.kind === "pay_as_you_go") return d.payAsYouGo;
  return d.free;
}

const dateFormat = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" });

// Every figure here comes from the detail DTO as sent. Sections with nothing
// to show do not render.
export default function DetailsTab({ contest }: { contest: ContestDetail }) {
  const d = copy.contest.details;
  const prize = prizeValue(contest);
  const ends = contest.ends_at ? new Date(contest.ends_at) : null;
  const properties: Property[] = [
    { ...prize, testId: "prop-prize" },
    { label: d.admission, value: admissionValue(contest), testId: "prop-admission" },
    {
      label: d.seats,
      value: d.seatsValue(contest.seats_taken, contest.seats_total),
      sublabel: d.seatsLeft(Math.max(0, contest.seats_total - contest.seats_taken)),
      testId: "prop-seats",
    },
    ...(contest.genre ? [{ label: d.genre, value: contest.genre, testId: "prop-genre" }] : []),
    ...(contest.challenge ? [{ label: d.challenge, value: contest.challenge, testId: "prop-challenge" }] : []),
    ...(contest.duration ? [{ label: d.duration, value: contest.duration }] : []),
    ...(ends && Number.isFinite(ends.getTime()) ? [{ label: d.ends, value: dateFormat.format(ends) }] : []),
  ];
  // A sample-pack prize already says so; otherwise the pack is an extra.
  if (contest.has_sample_pack && contest.prize.kind !== "prize") properties.push({ label: d.extras, value: d.samplePack });

  return (
    <div className="space-y-9">
      <section>
        <Eyebrow>{d.properties}</Eyebrow>
        <div className="mt-4">
          <PropertyGrid properties={properties} />
        </div>
      </section>

      {contest.winners.length > 0 && (
        <section>
          <Eyebrow>{d.winners}</Eyebrow>
          <ul className="mt-4 space-y-2.5">
            {contest.winners.map((w) => (
              <li key={`${w.placement}-${w.username}`} className="flex items-center gap-3 rounded-xl border border-gold/30 bg-ground-raised p-4">
                <Avatar username={w.username} logoUrl={w.logo_url} size="h-11 w-11" />
                <p className="min-w-0 flex-1 truncate text-sm font-bold text-chrome">@{w.username}</p>
                {w.placement > 0 && (
                  <span className="bf-tabular font-headline text-lg uppercase text-gold">{d.place(w.placement)}</span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {contest.sponsor && (
        <section>
          <Eyebrow>{d.sponsor}</Eyebrow>
          <div className="mt-4 flex items-center gap-3 rounded-xl border border-ground-edge bg-ground-raised p-4">
            <Avatar username={contest.sponsor.username} logoUrl={contest.sponsor.logo_url} size="h-12 w-12" />
            <p className="min-w-0 truncate text-sm font-bold text-chrome">@{contest.sponsor.username}</p>
          </div>
        </section>
      )}

      {contest.viewer.can_judge && (
        <section className="rounded-xl border border-ground-edge bg-ground-raised p-4">
          <p className="text-[12px] text-chrome-dim">{d.judgeNote}</p>
          <a href={`/contest/judge/${encodeURIComponent(contest.id)}`} className={`${secondaryButton} mt-3`}>
            <Icon name="trophy" className="h-4 w-4" />
            {d.judge}
          </a>
        </section>
      )}
    </div>
  );
}
